import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useRef } from "react";
import { HistoryState, useHistoryState } from "@uidotdev/usehooks";

interface UndoRedoProviderProps<S> extends PropsWithChildren {
  initialState?: S;
}

export type HistoryEventType = "undo" | "redo" | "set" | null;

export interface HistorySnapshot<S> {
  state: S;
  eventType: HistoryEventType;
}

export type ExtendedHistoryState<S> = HistoryState<S> & {
  subscribe: (callback: () => void) => () => void;
  getSnapshot: () => HistorySnapshot<S>;
};

export const HistoryContext = createContext<ExtendedHistoryState<any>>(undefined!);

const HistoryProvider = <S,>({ children, initialState }: UndoRedoProviderProps<S>): JSX.Element => {
  const history = useHistoryState<S>(initialState);
  const listeners = useRef<Set<() => void>>(new Set());
  const lastEventType = useRef<HistoryEventType>(null);
  const snapshotRef = useRef<HistorySnapshot<S>>({ state: history.state, eventType: null });

  // Update snapshot whenever history.state changes AND notify listeners
  useEffect(() => {
    snapshotRef.current = {
      state: history.state,
      eventType: lastEventType.current,
    };

    // Only notify listeners if there's an event type (meaning an action was taken)
    if (lastEventType.current) {
      listeners.current.forEach((listener) => listener());
    }

    // Reset event type after snapshot is updated and listeners notified
    lastEventType.current = null;
  }, [history.state]);

  // Subscribe function for useSyncExternalStore
  const subscribe = useCallback((callback: () => void) => {
    listeners.current.add(callback);
    return () => {
      listeners.current.delete(callback);
    };
  }, []);

  // Get current snapshot
  const getSnapshot = useCallback((): HistorySnapshot<S> => {
    return snapshotRef.current;
  }, []);

  // Wrap set to track event type (listeners notified by useEffect)
  const set = useCallback(
    (newState: S) => {
      lastEventType.current = "set";
      history.set(newState);
    },
    [history]
  );

  // Wrap undo to track event type (listeners notified by useEffect)
  const undo = useCallback(() => {
    lastEventType.current = "undo";
    return history.undo();
  }, [history]);

  // Wrap redo to track event type (listeners notified by useEffect)
  const redo = useCallback(() => {
    lastEventType.current = "redo";
    return history.redo();
  }, [history]);

  const extendedHistory: ExtendedHistoryState<S> = useMemo(
    () => ({
      ...history,
      set,
      undo,
      redo,
      subscribe,
      getSnapshot,
    }),
    [history, set, undo, redo, subscribe, getSnapshot]
  );

  return <HistoryContext.Provider value={extendedHistory}>{children}</HistoryContext.Provider>;
};

export default HistoryProvider;
